// TorqDashboardTable.js - Fully dynamic table component with copy functionality
import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  makeStyles,
  Box,
} from "@material-ui/core";
import StyledTableRow from "../../components/StyledTableRow";
import CopyTablesObject from "../../components/xlsx-export/CopyTablesObject";
import { countOrACVFormatter } from "../../util/customFunctions";
import Tooltip from "@material-ui/core/Tooltip";
import { withStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: "inline-block",
    paddingBottom: "2rem",
    paddingTop: "1rem",
    width: "100%",
  },
  tableContainer: {
    maxHeight: "85vh",
    border: "1px solid #e0e0e0",
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
    overflowX: "auto",
    overflowY: "auto",
    position: "relative",
  },
  table: {
    borderCollapse: "collapse",
  },
  // Header cells - First row (Solution Category)
  solutionCategoryHeaderCell: {
    fontWeight: 700,
    border: "1px solid #e0e0e0",
    padding: "8px",
    position: "sticky",
    backgroundColor: "white",
    left: 0,
    top: 0,
    zIndex: 15,
    minWidth: "150px",
    textAlign: "center",
  },
  solutionCategoryHeaderCellSticky: {
    fontWeight: 700,
    border: "1px solid #e0e0e0",
    borderRight: "2px solid #bdbdbd",
    padding: "8px",
    position: "sticky",
    backgroundColor: "white",
    left: 0,
    top: 0,
    zIndex: 15,
    minWidth: "150px",
    textAlign: "center",
  },
  solutionCategoryCell: {
    fontWeight: 600,
    border: "1px solid #e0e0e0",
    padding: "8px",
    textAlign: "center",
    backgroundColor: "#4471c4",
    color: "white",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  // Header cells - Second row (Customer Segment) - with box-shadow for bottom border
  customerSegmentHeaderCell: {
    fontWeight: 600,
    border: "1px solid #e0e0e0",
    padding: "8px",
    textAlign: "center",
    backgroundColor: "white",
    position: "sticky",
    left: 0,
    top: 41,
    zIndex: 14,
    minWidth: "150px",
    boxShadow: "inset 0 -1px 0 0 #e0e0e0", // Creates bottom border
  },
  customerSegmentHeaderCellSticky: {
    fontWeight: 600,
    border: "1px solid #e0e0e0",
    borderRight: "2px solid #bdbdbd",
    padding: "8px",
    textAlign: "center",
    backgroundColor: "white",
    position: "sticky",
    left: 0,
    top: 41,
    zIndex: 14,
    minWidth: "150px",
    boxShadow: "inset 0 -1px 0 0 #e0e0e0", // Creates bottom border
  },
  segmentCell: {
    fontWeight: 600,
    border: "1px solid #e0e0e0",
    padding: "8px",
    textAlign: "center",
    backgroundColor: "white",
    position: "sticky",
    top: 41,
    zIndex: 9,
    boxShadow: "inset 0 -1px 0 0 #e0e0e0", // Creates bottom border
  },
  // Data row cells
  rowLabelCell: {
    fontWeight: 600,
    border: "1px solid #e0e0e0",
    padding: "8px",
    backgroundColor: "white",
    position: "sticky",
    left: 0,
    zIndex: 5,
    minWidth: "150px",
    textAlign: "left",
  },
  rowLabelCellSticky: {
    fontWeight: 600,
    border: "1px solid #e0e0e0",
    borderRight: "2px solid #bdbdbd",
    padding: "8px",
    backgroundColor: "white",
    position: "sticky",
    left: 0,
    zIndex: 5,
    minWidth: "150px",
    textAlign: "left",
  },
  rowLabelCellGray: {
    fontWeight: 600,
    border: "1px solid #e0e0e0",
    padding: "8px",
    backgroundColor: "#f5f5f5",
    position: "sticky",
    left: 0,
    zIndex: 5,
    minWidth: "150px",
    textAlign: "left",
  },
  rowLabelCellGraySticky: {
    fontWeight: 600,
    border: "1px solid #e0e0e0",
    borderRight: "2px solid #bdbdbd",
    padding: "8px",
    backgroundColor: "#f5f5f5",
    position: "sticky",
    left: 0,
    zIndex: 5,
    minWidth: "150px",
    textAlign: "left",
  },
  dataCell: {
    border: "1px solid #e0e0e0",
    padding: "8px",
    textAlign: "right",
    minWidth: "100px",
  },
  clickableCell: {
    border: "1px solid #e0e0e0",
    padding: "8px",
    textAlign: "right",
    minWidth: "100px",
    cursor: "pointer",
    transition: "background-color 0.2s",
    // "&:hover": {
    //   backgroundColor: "#e3f2fd",
    // },
  },
}));

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: "rgb(255,255,255,0.8)",
    color: "black",
    fontFamily: "roboto",
    fontSize: theme.typography.pxToRem(14),
    borderRadius: "1rem",
    padding: "8px 12px",
    boxShadow: "0 0.1rem 0.1rem 0 rgba(0,0,0,0.2)",
    marginTop: "0.4rem",
  },
}))(Tooltip);

const formatCurrency = (value) => {
  if (!value && value !== 0) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercentage = (value) => {
  if (!value && value !== 0) return "-";
  return `${(value * 100).toFixed(2)}%`;
};

const TorqDashboardTable = ({ 
  data = [], 
  uniqueSolutionCategories = [], 
  uniqueSegments = [], 
  uniqueStages = [],
  winRateData = [],
  quarter = "",
  dataTest,
  handleChangeLocation = () => {}
}) => {
  const classes = useStyles();
  const tableRef = useRef();
  const tableContainerRef = useRef();
  
  const [isHorizontallyScrolled, setIsHorizontallyScrolled] = useState(false);

  const solutionCategories = uniqueSolutionCategories;
  const hasNoData = !data || data.length === 0 || !solutionCategories || solutionCategories.length === 0;

  // Get data for a specific combination
  const getData = (solutionCat, segment, stage) => {
    return data.find(
      (item) =>
        item.Solution_Cat === solutionCat &&
        item.Acct_Size_Segment === segment &&
        item.final_stage_for_closed === stage
    );
  };

  const getWinRateData = (solutionCat, segment) => {
    return winRateData.find(
      (item) =>
        item.Solution_Cat === solutionCat &&
        item.Acct_Size_Segment === segment 
    );
  };

  // Handle scroll to detect horizontal scrolling
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer) return;

    const handleScroll = () => {
      setIsHorizontallyScrolled(tableContainer.scrollLeft > 0);
    };

    tableContainer.addEventListener("scroll", handleScroll);
    return () => tableContainer.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle cell click
  const handleCellClick = (stage, solutionCat, segment, isClosedWon = false) => {
    const lastStage = isClosedWon || stage === uniqueStages[uniqueStages.length - 1];
    const segmentArray = segment === "Total" ? uniqueSegments.filter(s => s !== "Total") : [segment];
    const solutionCategory = solutionCat;
    
    handleChangeLocation(
      "drilldownOnGraph",
      stage,
      lastStage,
      "cameTo",
      segmentArray,
      solutionCategory
    );
  };

  // Create stage mapping for row labels
  const stageRowMapping = {};
  uniqueStages.forEach(stage => {
    stageRowMapping[stage] = `# of ${stage}`;
  });

  // Define the rows dynamically from uniqueStages
  const tableRows = uniqueStages.map(stage => ({
    label: stageRowMapping[stage] || stage,
    stage: stage,
    isClickable: true,
    getValue: (solutionCat, segment) => {
      const d = getData(solutionCat, segment, stage);
      return d?.count ? countOrACVFormatter(d.count) : '-';
    },
    onClick: (solutionCat, segment) => {
      handleCellClick(stage, solutionCat, segment, false);
    },
  }));

  // Add additional calculated rows
  const additionalRows = [
    {
      label: `${uniqueStages[uniqueStages.length - 1]} $`,
      stage: uniqueStages[uniqueStages.length - 1],
      isClickable: true,
      getValue: (solutionCat, segment) => {
        const d = getData(solutionCat, segment, uniqueStages[uniqueStages.length - 1]);
        return d?.acv ? countOrACVFormatter(d.acv, true) : '-';
      },
      onClick: (solutionCat, segment) => {
        handleCellClick(uniqueStages[uniqueStages.length - 1], solutionCat, segment, true);
      },
    },
    {
      label: `ASP for Won`,
      isClickable: false,
      getValue: (solutionCat, segment) => {
        const d = getWinRateData(solutionCat, segment);
        return d?.asp ? countOrACVFormatter(d.asp, true) : '-';
      },
    },
    {
      label: `Win Rate from ${uniqueStages[0]}`,
      isClickable: false,
      getValue: (solutionCat, segment) => {
        const d = getWinRateData(solutionCat, segment);
        return d?.win_rate ? formatPercentage(d.win_rate) : "0%";
      },
    },
    {
      label: `Win Rate from ${uniqueStages[1]}`,
      isClickable: false,
      getValue: (solutionCat, segment) => {
        const d = getWinRateData(solutionCat, segment);
        return d?.win_rate_by_second_Qual_Stage ? formatPercentage(d.win_rate_by_second_Qual_Stage) : "0%";
      },
    },
    {
      label: `Avg. Time from ${uniqueStages[0]} to Won (Days)`,
      isClickable: false,
      getValue: (solutionCat, segment) => {
        const d = getWinRateData(solutionCat, segment);
        return d?.avgTime ? countOrACVFormatter(d.avgTime) : '-';
      },
    },
    {
      label: (
        <HtmlTooltip
          placement="right"
          title={
            <>
              <strong>$ per day for Won</strong>
              = Sum(All $ Won)
              / Sum(Avg. days from Qualification to Won × # of Opps Won)
            </>
          }
        >
          <span style={{ cursor: "pointer" }}>
            $ per day for Won
          </span>
        </HtmlTooltip>
      ),
      isClickable: false,
      getValue: (solutionCat, segment) => {
        const d = getWinRateData(solutionCat, segment);
        return d?.valuePerDay ? countOrACVFormatter(d.valuePerDay) : 0;
      },
    },
  ];

  const allRows = [...tableRows, ...additionalRows];

  // Helper function to get row label cell class 
  const getRowLabelClass = (index) => {
    const isGray = index % 2 === 1;
    if (isHorizontallyScrolled) {
      return isGray ? classes.rowLabelCellGraySticky : classes.rowLabelCellSticky;
    }
    return isGray ? classes.rowLabelCellGray : classes.rowLabelCell;
  };

  return (
    <div className={classes.wrapper}>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <CopyTablesObject tableRef={tableRef} />
      </Box>

      <TableContainer 
        component={Paper} 
        className={classes.tableContainer}
        ref={tableContainerRef}
      >
        <Table 
          ref={tableRef}
          size="small"
          className={classes.table}
          data-test={dataTest}
        >
          <TableHead>
            {/* First Header Row - Solution Category */}
            <TableRow>
              <TableCell 
                className={
                  isHorizontallyScrolled 
                    ? classes.solutionCategoryHeaderCellSticky 
                    : classes.solutionCategoryHeaderCell
                }
              >
                Solution Category
              </TableCell>
              {solutionCategories.map((solutionCat) => (
                <TableCell
                  key={solutionCat}
                  colSpan={uniqueSegments.length}
                  className={classes.solutionCategoryCell}
                >
                  {solutionCat}
                </TableCell>
              ))}
            </TableRow>

            {/* Second Header Row - Customer Segment */}
            <TableRow>
              <TableCell 
                className={
                  isHorizontallyScrolled 
                    ? classes.customerSegmentHeaderCellSticky 
                    : classes.customerSegmentHeaderCell
                }
              >
                Customer Segment
              </TableCell>
              {solutionCategories.map((solutionCat) => (
                <React.Fragment key={solutionCat}>
                  {uniqueSegments.map((segment) => (
                    <TableCell key={segment} className={classes.segmentCell}>
                      {segment}
                    </TableCell>
                  ))}
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {allRows.map((row, index) => {
              const isGrayRow = index % 2 === 1;
              return (
                <TableRow 
                  key={row.label + index}
                  style={{ backgroundColor: isGrayRow ? "#f5f5f5" : "white" }}
                >
                  <TableCell className={getRowLabelClass(index)}>
                    {row.label}
                  </TableCell>
                  {solutionCategories.map((solutionCat) => (
                    <React.Fragment key={solutionCat}>
                      {uniqueSegments.map((segment) => (
                        <TableCell 
                          key={segment} 
                          className={row.isClickable ? classes.clickableCell : classes.dataCell}
                          onClick={() => {
                            if (row.isClickable && row.onClick) {
                              row.onClick(solutionCat, segment);
                            }
                          }}
                          title={row.isClickable ? "Click to view details" : ""}
                        >
                          {row.getValue(solutionCat, segment)}
                        </TableCell>
                      ))}
                    </React.Fragment>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default TorqDashboardTable;